import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosError } from 'axios';
import { env } from '@/config/env';
import { API_ROUTES, APP_ROUTES } from '@/config/constants';
import { AppError } from './errors';
import type { ApiError, ApiValidationError } from '@/types/api';
import { extractFilenameFromHeader, type DownloadResult } from '@/lib/utils';

/**
 * Axios instance configured for the API
 * - Base URL from environment (without a version)
 * - Credentials included for httpOnly cookies
 * - JSON content type
 */
const axiosInstance: AxiosInstance = axios.create({
  baseURL: `${env.API_URL}/api`,
  timeout: 30000,
  withCredentials: true, // Required for httpOnly cookies
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

/**
 * Helper to get cookie value
 */
function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() ?? null;
  }
  return null;
}

/**
 * Fetch CSRF token from backend
 * Should be called when the app initializes
 */
export async function initCsrf(): Promise<void> {
  try {
    await axiosInstance.get(API_ROUTES.AUTH.CSRF);
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
  }
}

/**
 * Request interceptor
 * - Add CSRF token if available
 */
axiosInstance.interceptors.request.use(
  (config) => {
    // Get CSRF token from cookie if exists
    const csrfToken = getCookie('csrftoken');
    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Flag to prevent multiple refresh attempts
 */
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

/**
 * URLs that should not trigger token refresh on 401
 * These are auth endpoints where 401 means "don't retry"
 * Note: /auth/me/ is NOT included so it CAN attempt refresh on page reload
 */
const AUTH_URLS = [API_ROUTES.AUTH.LOGIN, API_ROUTES.AUTH.REFRESH, API_ROUTES.AUTH.CSRF];

/**
 * Response interceptor
 * - Handle 401 with automatic token refresh
 * - Transform errors to AppError
 */
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError | ApiValidationError>) => {
    const originalRequest = error.config;

    // If no config or no response, reject immediately
    if (!originalRequest || !error.response) {
      return Promise.reject(AppError.fromAxiosError(error));
    }

    const requestUrl = originalRequest.url ?? '';

    // Handle 401 - Unauthorized
    if (error.response.status === 401) {
      // Don't try to refresh if we're already on auth endpoints
      if (AUTH_URLS.some((url) => requestUrl.includes(url))) {
        return Promise.reject(AppError.fromAxiosError(error));
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => axiosInstance(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        // Attempt to refresh the token
        await axiosInstance.post(API_ROUTES.AUTH.REFRESH);

        // Refresh successful, process queued requests
        processQueue(null);

        // Retry the original request
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // Refresh failed, reject all queued requests
        processQueue(refreshError as Error);

        // Redirect to login only if not already there (prevents infinite loop)
        if (!window.location.pathname.includes(APP_ROUTES.SIGNIN)) {
          window.location.href = APP_ROUTES.SIGNIN;
        }

        return Promise.reject(AppError.fromAxiosError(error));
      } finally {
        isRefreshing = false;
      }
    }

    // Handle 403 - Forbidden
    if (error.response.status === 403) {
      // window.location.href = APP_ROUTES.FORBIDDEN;
      return Promise.reject(AppError.fromAxiosError(error));
    }

    // Transform to AppError for other errors
    return Promise.reject(AppError.fromAxiosError(error));
  }
);

/**
 * API Client with typed methods
 * Abstracts axios and provides consistent interface
 */
export const apiClient = {
  /**
   * GET request
   */
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await axiosInstance.get<T>(url, config);
    return response.data;
  },

  /**
   * POST request
   */
  async post<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
    const response = await axiosInstance.post<T>(url, data, config);
    return response.data;
  },

  /**
   * PUT request
   */
  async put<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
    const response = await axiosInstance.put<T>(url, data, config);
    return response.data;
  },

  /**
   * PATCH request
   */
  async patch<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
    const response = await axiosInstance.patch<T>(url, data, config);
    return response.data;
  },

  /**
   * DELETE request
   */
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await axiosInstance.delete<T>(url, config);
    return response.data;
  },

  /**
   * Download request - returns blob with filename from Content-Disposition header
   *
   * @param url - The URL to download from
   * @param config - Optional axios config (params, etc.)
   * @param fallbackFilename - Fallback filename if server doesn't provide one
   * @returns Object with blob and filename
   */
  async download(
    url: string,
    config?: AxiosRequestConfig,
    fallbackFilename: string = 'download'
  ): Promise<DownloadResult> {
    const response = await axiosInstance.get<Blob>(url, {
      ...config,
      responseType: 'blob',
    });

    const contentDisposition = response.headers['content-disposition'];
    const filename = extractFilenameFromHeader(contentDisposition, fallbackFilename);

    return {
      blob: response.data,
      filename,
    };
  },
};

// Export the axios instance for advanced use cases
export { axiosInstance };
